import { IButtonStyles, IPlainCardProps, PersonaInitialsColor, getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIconButton, IPText, IPTooltipHost } from 'baseComponents';
import { IPHoverCard, IPHoverCardType } from 'baseComponents/HoverCard/IPHoverCard';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IUserRoleGroup, getUserRoleGroupItem, renderUserRoleGroupItem } from 'components/Pickers/UserRoleGroupPicker';
import {
  cancelIcon,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapSmall,
  globalTextStylesBold,
} from 'globalStyles';
import { Role } from 'models/auth/role';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { appRoleSetTypes, appRoleSets } from 'services/Auth/appRoles';
import { sortOnString } from 'utils/sorting';
import { overflow } from 'utils/string';

interface IRoleTagProps {
  role: Role;
  compact?: boolean;
  allowHoverCard: boolean;
  isMaxSystemRoles?: boolean;
  hideBackground?: boolean;
  textColor?: string;
  onRemove?: (item: Role) => void;
}

const RoleTag = (props: IRoleTagProps) => {
  const appContext = useContext(AppContext);
  const theme = getTheme();
  const { t } = useTranslation(['translation', 'adminAuth']);
  const size = props.compact ? IPPersonaSize.size24 : IPPersonaSize.size40;
  const name =
    props.role.name + (props.allowHoverCard ? ` (${props.role.users.length + props.role.groups.length})` : '');
  const cannotAddLicensedRole =
    props.isMaxSystemRoles && appRoleSets[appRoleSetTypes.Licensed].includes(props.role.systemRoleId ?? 0);

  const tagButtonStyles: IButtonStyles = {
    root: {
      borderRadius: '10px',
      height: props.compact ? '24px' : '40px',
      width: props.compact ? '24px' : '40px',
    },
  };

  const onRenderRoleCard = (item: Role): JSX.Element => {
    const count = item.users.length + item.groups.length;
    const items: IUserRoleGroup[] = [];

    if (count > 0) {
      items.push(...item.users.map((u) => getUserRoleGroupItem(u)));
      items.push(...item.groups.map((u) => getUserRoleGroupItem(u)));
    }

    items.sort((a, b) => sortOnString(a.name, b.name));

    return (
      <IPStack
        styles={{
          root: { width: 300, minHeight: 150, height: 300, maxHeight: '50vh', padding: 20 },
        }}
      >
        <IPStack
          verticalFill
          tokens={globalStackTokensGapSmall}
          styles={{
            root: { overflow: 'scroll' },
          }}
        >
          <IPStack.Item>
            <IPText styles={globalTextStylesBold}>
              {item.systemRoleId ? t('adminAuth:SystemRole.Members') : t('adminAuth:CustomRole.Members')}
            </IPText>
          </IPStack.Item>
          {count > 0 &&
            items.map((item) => {
              return renderUserRoleGroupItem(item, true, undefined, true, 0, props.hideBackground);
            })}
          {count === 0 && (
            <IPText>
              {item.systemRoleId ? t('adminAuth:SystemRole.NoMembers') : t('adminAuth:CustomRole.NoMembers')}
            </IPText>
          )}
        </IPStack>
      </IPStack>
    );
  };

  const plainCardProps: IPlainCardProps = {
    onRenderPlainCard: onRenderRoleCard,
    renderData: props.role,
  };

  return (
    <IPHoverCard
      type={IPHoverCardType.plain}
      plainCardProps={plainCardProps}
      instantOpenOnClick={props.allowHoverCard}
      shouldBlockHoverCard={() => !props.allowHoverCard}
    >
      <IPStack
        horizontal
        tokens={globalStackTokensGapExtraSmall}
        verticalAlign="center"
        styles={
          props.hideBackground !== true
            ? {
                root: {
                  background: appContext.useDarkMode ? theme.palette.neutralSecondary : theme.palette.neutralLight,
                  borderRadius: '10px',
                  height: 1 + (props.compact ? 24 : 40),
                },
              }
            : undefined
        }
      >
        <IPStack.Item styles={cannotAddLicensedRole ? { root: { opacity: 0.5 } } : undefined}>
          <IPTooltipHost
            content={cannotAddLicensedRole ? t('adminAuth:TabRoles.Dialogs.MaxLicensesReached.SubText') : undefined}
          >
            <IPPersona
              text={name}
              secondaryText={overflow(props.role.description, 50)}
              size={size}
              hidePersonaDetails={false}
              imageInitials={props.role.systemRoleId ? 'S' : undefined}
              initialsColor={props.role.systemRoleId ? PersonaInitialsColor.lightRed : undefined}
              styles={{ primaryText: { color: props.textColor } }}
            />
          </IPTooltipHost>
        </IPStack.Item>
        {props.onRemove && (
          <IPStack.Item>
            <IPIconButton
              onClick={() => {
                if (props.onRemove) {
                  props.onRemove(props.role);
                }
              }}
              styles={tagButtonStyles}
              iconProps={cancelIcon}
            />
          </IPStack.Item>
        )}
      </IPStack>
    </IPHoverCard>
  );
};

export default RoleTag;
